/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

class SignatureInputHandler extends PictSectionInputExtension
{
	/**
	 * @param {any} pFable
	 * @param {Record<string, any> | undefined} pOptions
	 * @param {string | undefined} pServiceHash
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		// Modal drawing state (active only while the modal is open)
		/** @type {HTMLCanvasElement | null} */
		this._canvas = null;
		/** @type {CanvasRenderingContext2D | null} */
		this._ctx = null;
		/** @type {boolean} */
		this._isDrawing = false;
		/** @type {number} */
		this._lastX = 0;
		/** @type {number} */
		this._lastY = 0;
		/** @type {number} */
		this._strokePointCount = 0;
		/** @type {number} - current smoothed line width for pressure/velocity tapering */
		this._currentWidth = 2;
		/** @type {number} - timestamp of the last pointer move for velocity calculation */
		this._lastTime = 0;

		// Tracks whether user cleared the canvas (reset to pristine on apply)
		/** @type {boolean} */
		this._cleared = false;

		// Undo/Redo stacks (stored as canvas data URLs)
		/** @type {string[]} */
		this._undoStack = [];
		/** @type {string[]} */
		this._redoStack = [];

		// Current modal settings
		/** @type {string} */
		this._inkColor = '#000000';
		/** @type {number} */
		this._penThickness = 2;
		/** @type {boolean} */
		this._printName = true;
		/** @type {boolean} */
		this._printTitle = true;
		/** @type {boolean} */
		this._printDate = true;
		/** @type {string} */
		this._dateFormat = 'MM/DD/YYYY hh:mm A';
		/** @type {string} */
		this._timezone = 'UTC';

		// Track which input the modal is editing
		/** @type {string} */
		this._activeInputHTMLID = '';
		/** @type {string} */
		this._activeInputHash = '';
		/** @type {boolean} */
		this._activeIsTabular = false;
		/** @type {number | string | null} */
		this._activeRowIndex = null;

		// Bound event handlers (for cleanup)
		/** @type {((e: PointerEvent) => void) | null} */
		this._boundPointerDown = null;
		/** @type {((e: PointerEvent) => void) | null} */
		this._boundPointerMove = null;
		/** @type {((e: PointerEvent) => void) | null} */
		this._boundPointerUp = null;
	}

	// ── Element ID helpers ───────────────────────────────────────────

	/**
	 * @param {string} pInputHTMLID - The HTML ID of the hidden input this signature is for
	 */
	getPreviewHTMLID(pInputHTMLID)
	{
		return `#SIGNATURE-PREVIEW-FOR-${pInputHTMLID}`;
	}

	/**
	 * @param {string} pInputHTMLID - The HTML ID of the hidden input this signature is for
	 * @param {string | number} pRowIndex - The row index if this is a tabular input
	 */
	getTabularPreviewHTMLID(pInputHTMLID, pRowIndex)
	{
		return `#TABULAR-SIGNATURE-PREVIEW-FOR-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * @param {string} pInputHTMLID - The HTML ID of the hidden input this signature is for
	 */
	getInputHTMLID(pInputHTMLID)
	{
		return `#${pInputHTMLID}`;
	}

	/**
	 * @param {string} pInputHTMLID - The HTML ID of the hidden input this signature is for
	 * @param {string | number | undefined} pRowIndex - The row index if this is a tabular input
	 */
	getTabularInputHTMLID(pInputHTMLID, pRowIndex)
	{
		return `#TABULAR-DATA-FOR-${pInputHTMLID}-${pRowIndex}`;
	}

	injectCustomCSS()
	{
		if (document.getElementById('SignatureInputCustomStyles')) return;

		const style = document.createElement('style');
		style.id = 'SignatureInputCustomStyles';
		style.textContent = /*css*/`
			.signature-preview-image {
				border: 1px dashed hsl(0,0%,80%);
				border-radius: 4px;
				width: 100%;
				height: 120px;
				aspect-ratio: 5 / 1;
				object-fit: contain;
				object-position: center;
				background: transparent;
			}
			.signature-preview-image[disabled] {
				opacity: 0.6;
				cursor: not-allowed;
			}
			.signature-preview-image.print-only-signature-preview {
				border: none;
			}
			.signature-edit-button {
				position: absolute;
				top: 4px;
				right: 4px;
				z-index:1;
			}
			.signature-preview-container {
				position: relative;
				cursor: pointer;
			}
			.signature-preview-container.is-danger .signature-preview-image {
				border-color: var(--bulma-danger);
			}
			.signature-preview-container.is-success .signature-preview-image {
				border-color: var(--bulma-success);
			}
			/* Toolbar buttons intentionally inherit the default themed Bulma .button styling
			   so they follow light/dark; no hardcoded background (which didn't spin with the theme). */
		`;
		document.head.appendChild(style);
	}

	// ── Placeholder image generation ─────────────────────────────────

	/**
	 * Generates a transparent PNG with dim "add signature" text for the pristine state.
	 * @returns {string} data URL of the placeholder PNG
	 */
	generatePlaceholderImage()
	{
		const tmpCanvas = document.createElement('canvas');
		tmpCanvas.width = 400;
		tmpCanvas.height = 120;
		const tmpCtx = tmpCanvas.getContext('2d');

		if (tmpCtx)
		{
			// transparent background (default)
			tmpCtx.clearRect(0, 0, tmpCanvas.width, tmpCanvas.height);

			// dim placeholder text
			tmpCtx.fillStyle = 'rgba(180, 180, 180, 0.6)';
			tmpCtx.font = 'italic 15px sans-serif';
			tmpCtx.textAlign = 'center';
			tmpCtx.textBaseline = 'middle';
			tmpCtx.fillText('add signature', tmpCanvas.width / 2, tmpCanvas.height / 2);
		}

		return tmpCanvas.toDataURL('image/png');
	}

	// ── Preview management ───────────────────────────────────────────

	/**
	 * Sets the preview image src to either the stored value or the placeholder.
	 * @param {string} pInputHTMLID - The HTML ID of the hidden input this signature is for
	 * @param {string} pValue - base64 encoded PNG value (or empty)
	 * @param {boolean} [isTabular=false] - Whether this input is within a tabular section
	 * @param {any} [pRowIndex] - The row index if this is a tabular input
	 */
	updatePreviewImage(pInputHTMLID, pValue, isTabular = false, pRowIndex = null)
	{
		const tmpSelector = isTabular ? this.getTabularPreviewHTMLID(pInputHTMLID, pRowIndex) : this.getPreviewHTMLID(pInputHTMLID);
		const tmpElements = this.pict.ContentAssignment.getElement(tmpSelector);

		if (Array.isArray(tmpElements) && tmpElements.length > 0)
		{
			const tmpImg = tmpElements[0];
			if (tmpImg instanceof HTMLImageElement)
			{
				if (pValue)
				{
					// Value is stored as raw base64; add the data URI prefix for display
					tmpImg.src = pValue.startsWith('data:') ? pValue : `data:image/png;base64,${pValue}`;
				}
				else
				{
					tmpImg.src = this.generatePlaceholderImage();
				}
			}
		}
	}

	/**
	 * Updates the hidden input value and dispatches a change event.
	 * @param {string} pInputHTMLID - The HTML ID of the hidden input to update
	 * @param {string} pValue - base64 string (without data URI prefix)
	 * @param {boolean} [isTabular=false] - Whether this input is within a tabular section
	 * @param {any} [pRowIndex] - The row index if this is a tabular input
	 */
	updateHiddenInputValue(pInputHTMLID, pValue, isTabular = false, pRowIndex = null)
	{
		const tmpSelector = isTabular ? this.getTabularInputHTMLID(pInputHTMLID, pRowIndex) : this.getInputHTMLID(pInputHTMLID);
		const tmpElements = this.pict.ContentAssignment.getElement(tmpSelector);

		if (Array.isArray(tmpElements) && tmpElements.length > 0)
		{
			const tmpInput = tmpElements[0];
			if (tmpInput instanceof HTMLInputElement)
			{
				this.pict.ContentAssignment.assignContent(tmpSelector, pValue);
				const event = new Event('change', { bubbles: true });
				tmpInput.dispatchEvent(event);
			}
		}
	}

	// ── Canvas settings from PictForm ────────────────────────────────

	/**
	 * Reads CanvasSettings from the PictForm input descriptor using direct view/input lookup.
	 * @param {string} pViewHash - The view hash for direct lookup
	 * @param {string} pInputHash - The input hash to look up
	 * @returns {{ InkColor: string, PenThickness: number, PrintName: boolean, PrintTitle: boolean, PrintDate: boolean, DateFormat: string, Timezone: string }}
	 */
	getCanvasSettings(pViewHash, pInputHash)
	{
		const tmpDefaultTimezone = this.fable.options?.Timezone || this.pict.AppData?.CurrentUser?.Timezone || new Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
		const tmpDefaults = { InkColor: '#000000', PenThickness: 2, PrintName: true, PrintTitle: true, PrintDate: true, DateFormat: 'MM/DD/YYYY hh:mm A', Timezone: tmpDefaultTimezone };

		const tmpView = this.pict.views[pViewHash];
		if (!tmpView || !tmpView.getInputFromHash) return tmpDefaults;

		const tmpInput = tmpView.getInputFromHash(pInputHash);
		if (!tmpInput?.PictForm?.CanvasSettings) return tmpDefaults;

		return {
			InkColor: tmpInput.PictForm.CanvasSettings.InkColor || tmpDefaults.InkColor,
			PenThickness: tmpInput.PictForm.CanvasSettings.PenThickness || tmpDefaults.PenThickness,
			PrintName: tmpInput.PictForm.CanvasSettings.hasOwnProperty('PrintName') ? tmpInput.PictForm.CanvasSettings.PrintName : tmpDefaults.PrintName,
			PrintTitle: tmpInput.PictForm.CanvasSettings.hasOwnProperty('PrintTitle') ? tmpInput.PictForm.CanvasSettings.PrintTitle : tmpDefaults.PrintTitle,
			PrintDate: tmpInput.PictForm.CanvasSettings.hasOwnProperty('PrintDate') ? tmpInput.PictForm.CanvasSettings.PrintDate : tmpDefaults.PrintDate,
			DateFormat: tmpInput.PictForm.CanvasSettings.DateFormat || tmpDefaults.DateFormat,
			Timezone: tmpInput.PictForm.CanvasSettings.Timezone || tmpDefaults.Timezone,
		};
	}

	// ── Named color map ──────────────────────────────────────────────

	/**
	 * Resolves a color name or hex value to a hex code for use on the canvas.
	 * @param {string} pColor - The color name or hex code from the configuration
	 * @returns {string} hex color code (e.g. "#FF0000")
	 */
	resolveColor(pColor)
	{
		if (!pColor) return '#000000';

		// If it's already a hex code, return it directly
		if (pColor.startsWith('#')) return pColor;

		/** @type {Record<string, string>} */
		const tmpColorMap = {
			'black': '#000000',
			'blue': '#0000FF',
			'royal blue': '#4169E1',
			'royalblue': '#4169E1',
			'red': '#FF0000',
			'dark blue': '#00008B',
			'darkblue': '#00008B',
			'navy': '#000080',
			'green': '#008000',
			'dark green': '#006400',
			'purple': '#800080',
			'brown': '#8B4513',
			'gold': '#FFD700',
			'gray': '#808080',
			'grey': '#808080',
		};

		return tmpColorMap[pColor.toLowerCase()] || pColor;
	}

	// ── Modal / Canvas ───────────────────────────────────────────────

	/**
	 * Opens the signature drawing modal.
	 * @param {string} pInputHTMLID - The HTML ID of the hidden input this signature is for
	 * @param {string} pViewHash - The view hash for looking up canvas settings
	 * @param {string} pInputHash - The input hash for looking up canvas settings
	 * @param {boolean} [isTabular=false] - Whether this input is within a tabular section
	 * @param {any} [pRowIndex] - The row index if this is a tabular input
	 * @param {boolean} [pDisabled=false] - Whether the input is disabled
	 */
	openSignatureModal(pInputHTMLID, pViewHash, pInputHash, isTabular = false, pRowIndex = null, pDisabled = false)
	{
		if (pDisabled) return;

		// Store active context
		this._activeInputHTMLID = pInputHTMLID;
		this._activeInputHash = pInputHash;
		this._activeIsTabular = isTabular;
		this._activeRowIndex = pRowIndex;

		// Read configuration
		const tmpSettings = this.getCanvasSettings(pViewHash, pInputHash);
		this._inkColor = this.resolveColor(tmpSettings.InkColor);
		this._penThickness = tmpSettings.PenThickness;
		this._printName = tmpSettings.PrintName;
		this._printTitle = tmpSettings.PrintTitle;
		this._printDate = tmpSettings.PrintDate;
		this._dateFormat = tmpSettings.DateFormat;
		this._timezone = tmpSettings.Timezone;

		// Reset undo/redo stacks
		this._undoStack = [];
		this._redoStack = [];

		// Read current value so we can pre-populate the canvas
		const tmpInputSelector = isTabular ? this.getTabularInputHTMLID(pInputHTMLID, pRowIndex) : this.getInputHTMLID(pInputHTMLID);
		const tmpInputElements = this.pict.ContentAssignment.getElement(tmpInputSelector);
		let tmpCurrentValue = '';
		if (Array.isArray(tmpInputElements) && tmpInputElements.length > 0 && tmpInputElements[0] instanceof HTMLInputElement)
		{
			tmpCurrentValue = tmpInputElements[0].value || '';
		}

		// Size the modal canvas from the preview area — match its rendered height directly
		// so the signing area is visually consistent with the preview.
		const tmpPreviewSelector = isTabular ? this.getTabularPreviewHTMLID(pInputHTMLID, pRowIndex) : this.getPreviewHTMLID(pInputHTMLID);
		const tmpPreviewElements = this.pict.ContentAssignment.getElement(tmpPreviewSelector);
		let tmpCanvasWidth = 600;
		let tmpCanvasHeight = 120;
		if (Array.isArray(tmpPreviewElements) && tmpPreviewElements.length > 0 && tmpPreviewElements[0] instanceof HTMLElement)
		{
			const tmpPreviewRect = tmpPreviewElements[0].getBoundingClientRect();
			if (tmpPreviewRect.width > 0)
			{
				tmpCanvasWidth = Math.round(tmpPreviewRect.width * 2);
			}
			if (tmpPreviewRect.height > 0)
			{
				tmpCanvasHeight = Math.round(tmpPreviewRect.height);
			}
		}

		// Build watermark overlay HTML (hidden when editing an existing signature, shown after clear)
		const tmpUser = this.pict.AppData?.CurrentUser;
		let tmpWatermarkInnerHTML = '';

		// Bottom-left: title (bold) + bullet + name
		const tmpWatermarkTitle = this._printTitle ? (tmpUser?.Title || '').trim() : '';
		const tmpWatermarkName = this._printName ? ((tmpUser?.NameFirst || '') + ' ' + (tmpUser?.NameLast || '')).trim() : '';
		const tmpWatermarkParts = [];
		if (tmpWatermarkTitle) tmpWatermarkParts.push(`<strong style="color: rgba(200,200,200,0.6);">${tmpWatermarkTitle}</strong>`);
		if (tmpWatermarkName) tmpWatermarkParts.push(tmpWatermarkName);
		if (tmpWatermarkParts.length > 0)
		{
			tmpWatermarkInnerHTML += `<span style="position:absolute; bottom:8px; left:10px; font:11px sans-serif; color:rgba(200,200,200,0.6); pointer-events:none;">${tmpWatermarkParts.join(' &bull; ')}</span>`;
		}

		// Bottom-right: timestamp placeholder
		if (this._printDate)
		{
			tmpWatermarkInnerHTML += `<span style="position:absolute; bottom:8px; right:10px; font:11px sans-serif; color:rgba(200,200,200,0.6); pointer-events:none;">timestamp</span>`;
		}

		// Signature line / watermark divider
		tmpWatermarkInnerHTML += `<div style="position:absolute; bottom:25px; left:8px; right:8px; height:0; border-top:1px solid rgba(180,180,180,0.5); pointer-events:none;"></div>`;

		const tmpWatermarkHTML = `<div id="SignatureModal-WatermarkOverlay" style="pointer-events:none;${tmpCurrentValue ? ' display:none;' : ''}">${tmpWatermarkInnerHTML}</div>`;

		const tmpModalBodyTemplate = /*HTML*/`
			<div class="signature-modal-body">
				<div class="signature-toolbar level is-mobile mb-3">
					<div class="level-left">
						<div class="level-item">
							<div class="field has-addons mb-0">
								<p class="control">
									<button type="button" class="button is-small" id="SignatureModal-Undo" title="Undo" aria-label="Undo last stroke"
										onclick="_Pict.providers.SignatureProvider.handleUndo();">
										<span class="icon is-small"><i class="fas fa-undo"></i></span>
									</button>
								</p>
								<p class="control">
									<button type="button" class="button is-small" id="SignatureModal-Redo" title="Redo" aria-label="Redo last stroke"
										onclick="_Pict.providers.SignatureProvider.handleRedo();">
										<span class="icon is-small"><i class="fas fa-redo"></i></span>
									</button>
								</p>
								<p class="control">
									<button type="button" class="button is-small" id="SignatureModal-Clear" title="Clear"
										onclick="_Pict.providers.SignatureProvider.handleClear();">
										<span class="icon is-small"><i class="fas fa-eraser"></i></span>
										<span>Clear</span>
									</button>
								</p>
							</div>
						</div>
					</div>
					<div class="level-right">
						<div class="level-item">
							<label class="label is-small pr-2" for="SignatureModal-PenThickness">Thickness</label>
							<input type="range" id="SignatureModal-PenThickness" class="slider" min="1" max="8" step="1" value="${this._penThickness}"
										oninput="_Pict.providers.SignatureProvider.handleThicknessChange(this.value);" />
						</div>
						<div class="level-item">
							<label class="label is-small pr-2" for="SignatureModal-InkColor">Ink Color</label>
							<input type="color" id="SignatureModal-InkColor" class="input is-small" value="${this._inkColor}"
								style="width:36px; cursor:pointer; padding:2px;"
								oninput="_Pict.providers.SignatureProvider.handleColorChange(this.value);" />
						</div>
					</div>
				</div>
				<div style="position:relative;">
					<canvas id="SignatureModal-Canvas"
						role="img" aria-label="Signature drawing area"
						style="border:1px solid hsl(0,0%,86%); border-radius:4px; cursor:crosshair; touch-action:none; width:100%; height:${tmpCanvasHeight}px; display:block; background:#fff;"
						width="${tmpCanvasWidth}" height="${tmpCanvasHeight}">
					</canvas>
					${tmpWatermarkHTML}
				</div>
			</div>`;

		// Use the field's label text as the modal title
		const tmpLabelElement = document.querySelector(`label[for="${pInputHTMLID}"]`);
		const tmpModalTitle = (tmpLabelElement && tmpLabelElement.textContent) ? tmpLabelElement.textContent.trim() : 'Signature';

		this.fable.HeadlightModal.spawnModal(
			{
				title: tmpModalTitle,
				modalBodyTemplate: tmpModalBodyTemplate,
				confirmText: 'Apply',
				cancelText: 'Cancel',
			},
			// On Apply
			() =>
			{
				this.applySignature();
			},
			// On Cancel (discard)
			null,
		);

		// Use a short timeout to ensure the modal DOM is ready before initializing the canvas
		setTimeout(() => this.initializeCanvas(tmpCurrentValue), 100);
	}

	/**
	 * Initializes the canvas within the modal, sets up drawing events, and loads existing signature data.
	 * @param {string} pExistingValue - base64 value of an existing signature (or empty)
	 */
	initializeCanvas(pExistingValue)
	{
		const tmpCanvasElement = document.getElementById('SignatureModal-Canvas');
		if (!tmpCanvasElement || !(tmpCanvasElement instanceof HTMLCanvasElement))
		{
			this.pict.log.error('[SignatureProvider] Could not find signature canvas in modal.');
			return;
		}
		this._canvas = tmpCanvasElement;
		this._ctx = this._canvas.getContext('2d');

		if (!this._ctx)
		{
			this.pict.log.error('[SignatureProvider] Could not get 2D context from canvas.');
			return;
		}

		// Scale canvas internal resolution by devicePixelRatio for crisp rendering on retina displays
		const tmpRect = this._canvas.getBoundingClientRect();
		const tmpDPR = window.devicePixelRatio || 1;
		if (tmpRect.width > 0)
		{
			this._canvas.width = tmpRect.width * tmpDPR;
			this._canvas.height = (tmpRect.height || 200) * tmpDPR;
			this._ctx.scale(tmpDPR, tmpDPR);
		}
		// Set initial drawing style
		this._ctx.lineCap = 'round';
		this._ctx.lineJoin = 'round';
		this._ctx.strokeStyle = this._inkColor;
		this._ctx.lineWidth = this._penThickness;
		this._ctx.imageSmoothingEnabled = true;

		// Fill with white background
		this._ctx.fillStyle = '#ffffff';
		this._ctx.fillRect(0, 0, this._canvas.width, this._canvas.height);

		// Load existing signature if we have one
		if (pExistingValue)
		{
			const tmpImage = new Image();
			tmpImage.onload = () =>
			{
				if (!this._ctx || !this._canvas) return;
				// Bypass the DPR scale transform to draw at native canvas resolution
				this._ctx.save();
				this._ctx.setTransform(1, 0, 0, 1, 0, 0);
				this._ctx.drawImage(tmpImage, 0, 0, this._canvas.width, this._canvas.height);
				this._ctx.restore();
				this.pushUndoState();
			};
			tmpImage.src = pExistingValue.startsWith('data:') ? pExistingValue : `data:image/png;base64,${pExistingValue}`;
		}
		else
		{
			this.pushUndoState();
		}

		// Bind pointer events for mouse and touch drawing
		this._boundPointerDown = (/** @type {PointerEvent} */ e) => this.onPointerDown(e);
		this._boundPointerMove = (/** @type {PointerEvent} */ e) => this.onPointerMove(e);
		this._boundPointerUp = (/** @type {PointerEvent} */ e) => this.onPointerUp(e);

		this._canvas.addEventListener('pointerdown', this._boundPointerDown);
		this._canvas.addEventListener('pointermove', this._boundPointerMove);
		this._canvas.addEventListener('pointerup', this._boundPointerUp);
		this._canvas.addEventListener('pointerleave', this._boundPointerUp);
	}

	// ── Drawing events ───────────────────────────────────────────────

	/**
	 * @param {PointerEvent} pEvent - The pointer event for pointerdown, used to start drawing on the canvas
	 */
	onPointerDown(pEvent)
	{
		if (!this._ctx || !this._canvas) return;
		pEvent.preventDefault();
		this._isDrawing = true;
		this._cleared = false;
		this._strokePointCount = 0;
		this._currentWidth = this._penThickness;
		this._lastTime = Date.now();
		const tmpPos = this.getCanvasPosition(pEvent);
		this._lastX = tmpPos.x;
		this._lastY = tmpPos.y;

		this._ctx.strokeStyle = this._inkColor;
		this._ctx.lineWidth = this._penThickness;
		this._ctx.beginPath();
		this._ctx.moveTo(this._lastX, this._lastY);
	}

	/**
	 * @param {PointerEvent} pEvent - The pointer event for pointermove, used to draw on the canvas.
	 * Uses quadratic Bézier curves through midpoints for smooth, natural-looking strokes.
	 */
	onPointerMove(pEvent)
	{
		if (!this._isDrawing || !this._ctx || !this._canvas) return;
		pEvent.preventDefault();

		const tmpPos = this.getCanvasPosition(pEvent);
		this._strokePointCount++;

		// ── Pressure / velocity-based width tapering ─────────────────
		const tmpMinWidth = this._penThickness * 0.3;
		const tmpMaxWidth = this._penThickness * 1.6;
		let tmpTargetWidth = this._penThickness;

		if (pEvent.pressure && pEvent.pressure > 0 && pEvent.pressure < 1)
		{
			// Hardware pressure available (stylus / Apple Pencil / pressure-capable trackpad)
			tmpTargetWidth = tmpMinWidth + (tmpMaxWidth - tmpMinWidth) * pEvent.pressure;
		}
		else
		{
			// No hardware pressure — derive from pointer velocity (faster = thinner)
			const tmpNow = Date.now();
			const tmpDt = tmpNow - this._lastTime;
			this._lastTime = tmpNow;

			if (tmpDt > 0)
			{
				const tmpDx = tmpPos.x - this._lastX;
				const tmpDy = tmpPos.y - this._lastY;
				const tmpDist = Math.sqrt(tmpDx * tmpDx + tmpDy * tmpDy);
				const tmpVelocity = tmpDist / tmpDt; // px per ms

				// Map velocity to width: slow strokes are thick, fast strokes are thin
				const tmpVelocityFactor = Math.min(tmpVelocity / 1.5, 1);
				tmpTargetWidth = tmpMaxWidth - (tmpMaxWidth - tmpMinWidth) * tmpVelocityFactor;
			}
		}

		// Smooth the width transition to avoid sudden jumps between segments
		this._currentWidth += (tmpTargetWidth - this._currentWidth) * 0.3;
		this._ctx.lineWidth = this._currentWidth;

		// ── Bézier midpoint smoothing ────────────────────────────────
		// Calculate the midpoint between the previous position and the current position
		const tmpMidX = (this._lastX + tmpPos.x) / 2;
		const tmpMidY = (this._lastY + tmpPos.y) / 2;

		// Draw a quadratic Bézier curve from the current path position to the midpoint,
		// using the previous position as the control point. This produces smooth C1-continuous
		// curves that join seamlessly at each midpoint.
		this._ctx.strokeStyle = this._inkColor;
		this._ctx.quadraticCurveTo(this._lastX, this._lastY, tmpMidX, tmpMidY);
		this._ctx.stroke();

		// Continue the path from the midpoint so the next segment connects smoothly
		this._ctx.beginPath();
		this._ctx.moveTo(tmpMidX, tmpMidY);

		this._lastX = tmpPos.x;
		this._lastY = tmpPos.y;
	}

	/**
	 * @param {PointerEvent} pEvent - The pointer event for pointerup or pointerleave
	 */
	onPointerUp(pEvent)
	{
		if (!this._isDrawing || !this._ctx) return;
		// Draw a final segment to the last known position so the stroke tip isn't clipped
		this._ctx.lineTo(this._lastX, this._lastY);
		this._ctx.stroke();
		this._isDrawing = false;
		this.pushUndoState();
	}

	/**
	 * Calculates the position relative to the canvas element.
	 * @param {PointerEvent} pEvent - The pointer event containing clientX and clientY
	 * @returns {{ x: number, y: number }}
	 */
	getCanvasPosition(pEvent)
	{
		if (!this._canvas) return { x: 0, y: 0 };
		const tmpRect = this._canvas.getBoundingClientRect();
		return {
			x: pEvent.clientX - tmpRect.left,
			y: pEvent.clientY - tmpRect.top,
		};
	}

	// ── Undo / Redo / Clear ──────────────────────────────────────────

	pushUndoState()
	{
		if (!this._canvas) return;
		this._undoStack.push(this._canvas.toDataURL('image/png'));
		// New action clears the redo stack
		this._redoStack = [];
	}

	handleUndo()
	{
		if (!this._canvas || this._undoStack.length <= 1) return;
		// Move the current state to the redo stack
		const tmpCurrent = this._undoStack.pop();
		if (tmpCurrent) this._redoStack.push(tmpCurrent);
		// Restore the previous state
		const tmpPrevious = this._undoStack[this._undoStack.length - 1];
		if (tmpPrevious) this.restoreCanvasFromDataURL(tmpPrevious);
	}

	handleRedo()
	{
		if (!this._canvas || this._redoStack.length < 1) return;
		const tmpState = this._redoStack.pop();
		if (!tmpState) return;
		this._undoStack.push(tmpState);
		this.restoreCanvasFromDataURL(tmpState);
	}

	handleClear()
	{
		if (!this._ctx || !this._canvas) return;
		this._ctx.fillStyle = '#ffffff';
		this._ctx.fillRect(0, 0, this._canvas.width, this._canvas.height);
		this._cleared = true;
		// Re-show the watermark overlay so the user sees the guides on the fresh canvas
		const tmpOverlay = document.getElementById('SignatureModal-WatermarkOverlay');
		if (tmpOverlay) tmpOverlay.style.display = '';
		this.pushUndoState();
	}

	/**
	 * Restores the canvas from a saved data URL.
	 * @param {string} pDataURL - The data URL to restore from
	 */
	restoreCanvasFromDataURL(pDataURL)
	{
		const tmpImage = new Image();
		tmpImage.onload = () =>
		{
			if (!this._ctx || !this._canvas) return;
			// Bypass the DPR scale transform to draw the snapshot pixel-for-pixel
			this._ctx.save();
			this._ctx.setTransform(1, 0, 0, 1, 0, 0);
			this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
			this._ctx.drawImage(tmpImage, 0, 0);
			this._ctx.restore();
		};
		tmpImage.src = pDataURL;
	}

	// ── Toolbar handlers ─────────────────────────────────────────────

	/**
	 * @param {string} pValue
	 */
	handleThicknessChange(pValue)
	{
		this._penThickness = parseInt(pValue, 10) || 2;
	}

	/**
	 * @param {string} pValue
	 */
	handleColorChange(pValue)
	{
		this._inkColor = pValue;
	}

	// ── Apply signature ──────────────────────────────────────────────

	/**
	 * Called when the user clicks "Apply" in the modal. Encodes the canvas to
	 * base64 PNG, writes it to the hidden input, and updates the preview image.
	 */
	applySignature()
	{
		if (!this._canvas || !this._ctx) return;

		// If the canvas was cleared, reset to pristine state
		if (this._cleared)
		{
			this.updateHiddenInputValue(this._activeInputHTMLID, '', this._activeIsTabular, this._activeRowIndex);
			this.updatePreviewImage(this._activeInputHTMLID, '', this._activeIsTabular, this._activeRowIndex);
			this.cleanupCanvasState();
			return;
		}

		// Use CSS-pixel dimensions for positioning (the DPR scale transform maps these to device pixels)
		const tmpDPR = window.devicePixelRatio || 1;
		const tmpW = this._canvas.width / tmpDPR;
		const tmpH = this._canvas.height / tmpDPR;

		// Draw signature line / watermark divider
		const tmpLineY = tmpH - 23;
		this._ctx.save();
		this._ctx.strokeStyle = 'rgba(180, 180, 180, 0.6)';
		this._ctx.lineWidth = 1;
		this._ctx.beginPath();
		this._ctx.moveTo(8, tmpLineY);
		this._ctx.lineTo(tmpW - 8, tmpLineY);
		this._ctx.stroke();
		this._ctx.restore();

		// Stamp identity and timestamp onto the canvas
		const tmpUser = this.pict.AppData?.CurrentUser;
		const tmpTitle = this._printTitle ? (tmpUser?.Title || '').trim() : '';
		const tmpPrintedName = this._printName ? ((tmpUser?.NameFirst || '') + ' ' + (tmpUser?.NameLast || '')).trim() : '';

		// Bottom-left: title (bold) + bullet + name
		const tmpIdentityParts = [];
		if (tmpTitle) tmpIdentityParts.push({ text: tmpTitle, bold: true });
		if (tmpPrintedName) tmpIdentityParts.push({ text: tmpPrintedName, bold: false });

		if (tmpIdentityParts.length > 0)
		{
			this._ctx.save();
			this._ctx.fillStyle = 'rgba(80, 80, 80, 0.7)';
			this._ctx.textAlign = 'left';
			this._ctx.textBaseline = 'bottom';

			let tmpX = 8;
			const tmpY = tmpH - 6;

			for (let i = 0; i < tmpIdentityParts.length; i++)
			{
				if (i > 0)
				{
					// Draw bullet divider between parts
					this._ctx.font = '11px sans-serif';
					this._ctx.fillText(' \u2022 ', tmpX, tmpY);
					tmpX += this._ctx.measureText(' \u2022 ').width;
				}
				this._ctx.font = tmpIdentityParts[i].bold ? 'bold 11px sans-serif' : '11px sans-serif';
				this._ctx.fillText(tmpIdentityParts[i].text, tmpX, tmpY);
				tmpX += this._ctx.measureText(tmpIdentityParts[i].text).width;
			}

			this._ctx.restore();
		}

		// Bottom-right: timestamp of signing
		const tmpTimestamp = this._printDate ? this.pict.providers.FlatpickrUtilProvider.formatDateWithTimezone('now', this._dateFormat, this._timezone) : '';
		if (tmpTimestamp)
		{
			this._ctx.save();
			this._ctx.font = '11px sans-serif';
			this._ctx.fillStyle = 'rgba(80, 80, 80, 0.7)';
			this._ctx.textAlign = 'right';
			this._ctx.textBaseline = 'bottom';
			this._ctx.fillText(tmpTimestamp, tmpW - 8, tmpH - 6);
			this._ctx.restore();
		}

		// Get the raw base64 data (strip the data URI prefix for storage)
		const tmpDataURL = this._canvas.toDataURL('image/png');
		const tmpBase64 = tmpDataURL.replace(/^data:image\/png;base64,/, '');
		// Write to hidden input
		this.updateHiddenInputValue(this._activeInputHTMLID, tmpBase64, this._activeIsTabular, this._activeRowIndex);
		// Update the preview image
		this.updatePreviewImage(this._activeInputHTMLID, tmpBase64, this._activeIsTabular, this._activeRowIndex);

		this.cleanupCanvasState();
	}

	/**
	 * Resets internal canvas/modal state after the modal closes.
	 */
	cleanupCanvasState()
	{
		if (this._canvas)
		{
			if (this._boundPointerDown) this._canvas.removeEventListener('pointerdown', this._boundPointerDown);
			if (this._boundPointerMove) this._canvas.removeEventListener('pointermove', this._boundPointerMove);
			if (this._boundPointerUp)
			{
				this._canvas.removeEventListener('pointerup', this._boundPointerUp);
				this._canvas.removeEventListener('pointerleave', this._boundPointerUp);
			}
		}

		this._canvas = null;
		this._ctx = null;
		this._isDrawing = false;
		this._cleared = false;
		this._undoStack = [];
		this._redoStack = [];
		this._activeInputHTMLID = '';
		this._activeInputHash = '';
		this._activeIsTabular = false;
		this._activeRowIndex = null;
	}

	// ── Standard lifecycle hooks ─────────────────────────────────────

	/**
	 * Handles displaying the signature value (or placeholder) when the form loads.
	 * @param {any} pInput - The input descriptor object
	 * @param {string} pValue - base64 encoded PNG value
	 * @param {boolean} [isTabular=false] - Whether this input is within a tabular section
	 * @param {number|string|null} [pRowIndex] - The row index if this is a tabular input
	 * @returns {void}
	 */
	handleInputValue(pInput, pValue, isTabular = false, pRowIndex = null)
	{
		this.updatePreviewImage(pInput.Macro.RawHTMLID, pValue, isTabular, pRowIndex);
	}

	/**
	 * @param {any} pView - The view object containing the input
	 * @param {any} pGroup - The group object containing the input
	 * @param {any} pRow - The row object containing the input (if applicable)
	 * @param {any} pInput - The input descriptor object
	 * @param {any} pValue - The current value for the input (base64 encoded PNG)
	 * @param {any} pHTMLSelector - The CSS selector for the input's HTML element
	 * @param {any} pTransactionGUID - The transaction GUID for this form load (if applicable)
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.handleInputValue(pInput, pValue, false);
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * @param {any} pView - The view object containing the input
	 * @param {any} pGroup - The group object containing the input
	 * @param {any} pInput - The input descriptor object
	 * @param {any} pValue 	- The current value for the input (base64 encoded PNG)
	 * @param {any} pHTMLSelector - The CSS selector for the input's HTML element
	 * @param {any} pRowIndex - The row index if this input is within a tabular section
	 * @param {any} pTransactionGUID - The transaction GUID for this form load (if applicable)
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this.handleInputValue(pInput, pValue, true, pRowIndex);
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * @param {any} pView - The view object containing the input
	 * @param {any} pGroup - The group object containing the input
	 * @param {any} pRow - The row object containing the input (if applicable)
	 * @param {any} pInput - The input descriptor object
	 * @param {any} pValue - The current value for the input (base64 encoded PNG)
	 * @param {any} pHTMLSelector - The CSS selector for the input's HTML element
	 * @param {any} pTransactionGUID - The transaction GUID for this form load (if applicable)
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.handleInputValue(pInput, pValue, false);
		this.injectCustomCSS();
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * @param {any} pView - The view object containing the input
	 * @param {any} pGroup - The group object containing the input
	 * @param {any} pInput - The input descriptor object
	 * @param {any} pValue - The current value for the input (base64 encoded PNG)
	 * @param {any} pHTMLSelector - The CSS selector for the input's HTML element
	 * @param {any} pRowIndex - The row index if this input is within a tabular section
	 * @param {any} pTransactionGUID - The transaction GUID for this form load (if applicable)
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this.handleInputValue(pInput, pValue, true, pRowIndex);
		this.injectCustomCSS();
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * @param {any} pView - The view object containing the input
	 * @param {any} pInput - The input descriptor object
	 * @param {any} pValue - The current value for the input (base64 encoded PNG)
	 * @param {any} pHTMLSelector - The CSS selector for the input's HTML element
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		const tmpValue = this.pict.ContentAssignment.readContent(pInput.Macro.RawHTMLID);
		this.pict.ContentAssignment.assignContent(pHTMLSelector, tmpValue);
		pView.dataChanged(pInput.Hash);
		return super.onDataRequest(pView, pInput, tmpValue, pHTMLSelector);
	}

	/**
	 * @param {any} pView - The view object containing the input
	 * @param {any} pInput - The input descriptor object
	 * @param {any} pValue - The current value for the input (base64 encoded PNG)
	 * @param {any} pHTMLSelector - The CSS selector for the input's HTML element
	 * @param {number} pRowIndex - The row index if this input is within a tabular section
	 */
	onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex)
	{
		const tmpValue = this.pict.ContentAssignment.readContent(this.getTabularInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex));
		this.pict.ContentAssignment.assignContent(pHTMLSelector, tmpValue);
		pView.dataChangedTabular(pInput.PictForm.GroupIndex, pInput.PictForm.InputIndex, pRowIndex);
		return super.onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex);
	}
}

module.exports = SignatureInputHandler;
